import {captureException} from '@sentry/browser';
import moment from 'moment';
import {
  A_DAY,
  BRANCH_MAPPING,
  BRANCH_NAME_MAPPING,
  HTTP_STATUS,
  JJ_DOMAIN,
  PAGE_SIZE,
  SEARCH_ADVANCED_PARAMS,
  SORT_TYPES,
  STANDARD_FORMAT,
} from '../constants/constants';
import {
  setBranch,
  setBranchOpeningTimes,
  setNearestBranch,
} from '../actions/branch.action';
import productApi from '../api/product.api';
import {
  checkProductDetails,
  getBranchFromUrl,
  getCategoryName,
  getCategoryNamesFromUrl,
  getKeyCaseInsensitive,
  getTodayDate,
  processedGQLSearchResults,
  processedProductDetail,
  titleCase,
  trimComma,
} from '../helpers/data.helper';
import {
  getFacetsFromServerSide,
  getSearchFromServerSide,
  getSearchPageFromServerSide,
  setProductDetail,
} from '../actions';
import SearchApi from '../api/search.api';
import {getApiConfig, parseConfig} from '../config/configProvider';
import {getOgData, getSchemaOpeningTimes} from '../helpers/seo.helper';
import {setCleanMode} from '../actions/cleanMode.action';
import {setShopBySectors} from '../actions/offers.action';
import {arrayToObject} from '../helpers/array.helper';
import {ServerCacheHelper} from '../helpers/ssr.helper';
import logo from '../images/logo.png';
import {setBlogCategoryBySlug, setBlogPost} from '../actions/blog.action';
import blogApi from '../api/blog.api';
import {strapiImage} from '../helpers/image.helper';
import jjContentApi from '../api/jjContent.api';
import {setJJContentBySlug} from '../actions/jjContent.action';
import {isGoogleBot} from '../helpers/browser.helper';
import cmsApi from '../api/cms.api';
import {setBanner} from '../actions/cms.action';
import * as ActionTypes from '../actions/types.action';

export const loadProductDetailsData = async (match, store, req) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  const config = getApiConfig();
  const branchFromUrl = getBranchFromUrl(match);
  const deliveryDate = moment(new Date()).format(STANDARD_FORMAT);
  const isGoogleBotRequest =
    isGoogleBot(req.get('User-Agent')) ||
    isGoogleBot(req.get('X-User-Agent')) ||
    req.get('X-Prerender') === '1';

  if (store) {
    store.dispatch(setBranch(branchFromUrl));
  }
  const isRunningInternalDefault =
    process.env.CONFIG_JS && process.env.NODE_LOCAL_DEV !== 'true';

  let productDetailDataAtFirstAvailableBranchPrice;

  try {
    const productWithFirstAvailableBranch = await productApi.getProduct({
      itemIds: [match.params && match.params.itemId],
      deliveryDate,
      branch: '',
      host: isRunningInternalDefault
        ? parseConfig(process.env.CONFIG_JS).graphQlInternal
        : config.graphQL,
    });
    const firstAvailableBranch =
      productWithFirstAvailableBranch &&
      productWithFirstAvailableBranch.data &&
      productWithFirstAvailableBranch.data.searchProductsByItemId &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results
        .length > 0 &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches.length > 0 &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches[0] &&
      productWithFirstAvailableBranch.data.searchProductsByItemId.results[0]
        .branches[0].locationId;

    const productAtFirstAvailableBranch = await productApi.getProduct({
      itemIds: [match.params && match.params.itemId],
      deliveryDate,
      branch: firstAvailableBranch,
      host: isRunningInternalDefault
        ? parseConfig(process.env.CONFIG_JS).graphQlInternal
        : config.graphQL,
    });
    const productDetailDataAtFirstAvailableBranch =
      processedProductDetail(
        productAtFirstAvailableBranch.data.searchProductsByItemId.results[0]
      ) || null;
    productDetailDataAtFirstAvailableBranchPrice =
      isGoogleBotRequest &&
      productDetailDataAtFirstAvailableBranch &&
      ((productDetailDataAtFirstAvailableBranch.collection &&
        productDetailDataAtFirstAvailableBranch.collection.price) ||
        productDetailDataAtFirstAvailableBranch.price);
  } catch (e) {}

  try {
    const branchSpecificProductDetail = await productApi.getProduct({
      itemIds: [match.params && match.params.itemId],
      deliveryDate,
      branch: branchFromUrl,
      host: isRunningInternalDefault
        ? parseConfig(process.env.CONFIG_JS).graphQlInternal
        : config.graphQL,
    });
    if (
      branchSpecificProductDetail &&
      branchSpecificProductDetail.errors &&
      branchSpecificProductDetail.errors.length > 0
    ) {
      return Promise.resolve({og: false});
    }
    if (checkProductDetails(branchSpecificProductDetail)) {
      const genericProductDetail = await productApi.getProduct({
        itemIds: [match.params && match.params.itemId],
        deliveryDate,
        branch: '',
        host: isRunningInternalDefault
          ? parseConfig(process.env.CONFIG_JS).graphQlInternal
          : config.graphQL,
      });
      const genericProductDetailData =
        processedProductDetail(
          genericProductDetail.data.searchProductsByItemId.results[0]
        ) || null;
      if (store && genericProductDetailData) {
        if (isGoogleBotRequest) {
          genericProductDetail.isGoogleBotRequest = isGoogleBotRequest;
        }
        if (!genericProductDetailData.collection) {
          genericProductDetailData.collection = {
            price:
              genericProductDetailData.price ||
              productDetailDataAtFirstAvailableBranchPrice,
          };
        }
        store.dispatch(setProductDetail(genericProductDetailData));
      }
      const og = getOgData(genericProductDetailData);
      return Promise.resolve(og);
    } else {
      const branchSpecificProductDetailData =
        processedProductDetail(
          branchSpecificProductDetail.data.searchProductsByItemId.results[0]
        ) || null;
      if (isGoogleBotRequest) {
        branchSpecificProductDetailData.isGoogleBotRequest = isGoogleBotRequest;
      }
      if (!branchSpecificProductDetailData.collection) {
        branchSpecificProductDetailData.collection = {
          price:
            branchSpecificProductDetailData.price ||
            productDetailDataAtFirstAvailableBranchPrice,
        };
      }
      if (store && branchSpecificProductDetailData) {
        store.dispatch(setProductDetail(branchSpecificProductDetailData));
      }
      const og = getOgData(branchSpecificProductDetailData);
      return Promise.resolve(og);
    }
  } catch (e) {
    console.log(e, 'ssr load data error');
    captureException(e);
    return Promise.resolve({og: false});
  }
};

export const loadSearchData = (match, store, req, isSpamUrl) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  let og;
  const config = getApiConfig();
  const host =
    process.env.CONFIG_JS && process.env.NODE_LOCAL_DEV !== 'true'
      ? parseConfig(process.env.CONFIG_JS).graphQlInternal
      : config.graphQL;
  const branchFromUrl = getBranchFromUrl(match);
  if (store && store.dispatch) {
    store.dispatch(setBranch(branchFromUrl));
  }
  const {brandName, category1Name, category2Name, category3Name} = match.params;
  const q = req && req.query && req.query.q;
  let params = Object.assign({}, SEARCH_ADVANCED_PARAMS, {
    b: branchFromUrl,
    size: PAGE_SIZE.M,
    deliveryDate: getTodayDate(),
  });
  if (brandName) {
    params = {
      ...params,
      ...{
        brand: decodeURIComponent(brandName),
      },
    };
  }
  const categoryNames = getCategoryNamesFromUrl(
    category1Name,
    category2Name,
    category3Name
  );
  if (categoryNames.length > 0) {
    params = {
      ...params,
      ...{
        sortType: SORT_TYPES.CATEGORY,
      },
      ...{
        categoryNames,
      },
    };
  } else {
    params.q = q;
  }
  let httpCode = isSpamUrl ? HTTP_STATUS.NOT_FOUND : HTTP_STATUS.OK;
  try {
    const getSearch = SearchApi.search(params, null, host);
    return Promise.all([getSearch]).then(responses => {
      if (
        responses[0] &&
        responses[0].data &&
        responses[0].data.searchProducts
      ) {
        if (responses[0].data.searchProducts.results) {
          const searchResults =
            processedGQLSearchResults(
              responses[0].data.searchProducts.results
            ) || [];
          if (searchResults && searchResults.length === 0) {
            httpCode = HTTP_STATUS.NOT_FOUND;
          }
          store.dispatch(getSearchFromServerSide(searchResults));
          store.dispatch(
            getSearchPageFromServerSide(responses[0].data.searchProducts.page)
          );
        }
        if (responses[0].data.searchProducts.facets) {
          store.dispatch(
            getFacetsFromServerSide(responses[0].data.searchProducts.facets)
          );
        }
        let title = '';
        if (brandName) {
          title = brandName;
        }
        if (category1Name) {
          title = category1Name;
        }
        if (category2Name) {
          title = category2Name;
        }
        if (category3Name) {
          title = category3Name;
        }
        if (categoryNames.length > 0) {
          title = getCategoryName(categoryNames);
        }
        title = titleCase(trimComma(title));
        og = {
          og: true,
          title: `Buy Wholesale ${title} From JJ Foodservice For Delivery or Collection`,
          description: `View and order online from our range of wholesale ${title} at JJ Foodservice. Open to all.`,
          httpCode,
        };
        return Promise.resolve(og);
      }
      return Promise.resolve({
        httpCode,
      });
    });
  } catch (e) {
    captureException(e);
  }
};

export const loadSearchResultsData = title => {
  return {
    og: true,
    title: `Buy Wholesale ${title} From JJ Foodservice For Delivery or Collection`,
    description: `View and order online from our range of wholesale ${title} at JJ Foodservice. Open to all.`,
  };
};

export const loadHomePageData = (match, store, req) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  const hostDomain = 'http://olympus-app-internal:81';
  // const hostDomain = `${req.protocol}://localhost:8081`;
  return jjContentApi
    .getContentBySlug('shop-by-sectors', hostDomain)
    .then(res => {
      if (store && res) {
        store.dispatch(setShopBySectors(res));
      }
      return cmsApi
        .getBanners(hostDomain)
        .then(banners => {
          if (banners) {
            store.dispatch(setBanner(banners));
          }
        })
        .finally(() => {
          return Promise.resolve({});
        });
    })
    .catch(e => {
      captureException(e);
      return Promise.resolve({});
    });
};

export const loadBranchLocatorData = (match, store) => {
  // only use for server side rendering
  if (typeof window !== 'undefined') {
    return Promise.resolve(null);
  }
  const defaultOGData = {
    og: true,
    title:
      'JJ Foodservice Branch Finder - Find Your Nearest Branch For Wholesale Food & Catering Supplies in England',
    description:
      'Enter your postcode on our branch finder tool to find your nearest JJ Foodservice branch in England for collection orders.',
  };
  const branchId = getKeyCaseInsensitive(
    BRANCH_NAME_MAPPING,
    match.params.branchName
  );
  if (!branchId) {
    return Promise.resolve(defaultOGData);
  }
  const cachedopeningTimes = ServerCacheHelper.get('branches');
  const ogBranchData = ServerCacheHelper.get(`ogData-${branchId}`);
  if (cachedopeningTimes) {
    store.dispatch(setBranchOpeningTimes(cachedopeningTimes));
  }
  if (cachedopeningTimes && ogBranchData) {
    return Promise.resolve({...defaultOGData, ogBranchData});
  }

  const openingTimes = arrayToObject(cachedopeningTimes, 'inventLocationId');
  const currentBranch = openingTimes[branchId];
  store.dispatch(setNearestBranch(currentBranch));
  const newOgBranchData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `JJ Foodservice ${currentBranch.branchName}`,
    '@id': `https://jjfoodservice.com/branch/${BRANCH_MAPPING[
      branchId
    ].toLowerCase()}`,
    url: `https://jjfoodservice.com/branch/${BRANCH_MAPPING[
      branchId
    ].toLowerCase()}`,
    description:
      'Enter your postcode on our branch finder tool to find your nearest JJ Foodservice branch in England for collection orders.',
    telephone: currentBranch.contact && currentBranch.contact.phone,
    email: currentBranch.contact && currentBranch.contact.email,
    logo: {
      '@type': 'ImageObject',
      url: `https://${JJ_DOMAIN.PROD}${logo}`,
      width: 560,
      height: 92,
    },
    image: logo,
    currenciesAccepted: 'GBP',
    paymentAccepted: 'Cash, Credit Card',
    hasMap: `https://www.google.com/maps/search/JJ+Foodservice+Limited+${encodeURI(
      currentBranch.branchName
    )}/`,
    sameAs: [
      'https://www.facebook.com/jjfoodservice',
      'https://www.youtube.com/channel/UCt4UlV3a_3DW_-Zlrqn9EVQ',
    ],
    address: [
      {
        '@type': 'PostalAddress',
        streetAddress:
          currentBranch.address &&
          currentBranch.address.address.replace(/(\r\n|\n|\r)/gm, ', '),
        addressRegion: currentBranch.city,
        postalCode: currentBranch.address && currentBranch.address.zipcode,
        addressCountry: 'England',
      },
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: currentBranch.contact && currentBranch.contact.phone,
        contactType: 'customer service',
      },
    ],
    geo: {
      '@type': 'GeoCoordinates',
      latitude: currentBranch.latitude,
      longitude: currentBranch.longitude,
    },
    openingHoursSpecification:
      currentBranch.collectionOpeningTime &&
      getSchemaOpeningTimes(currentBranch.collectionOpeningTime),
  };
  store.dispatch(setBranchOpeningTimes(openingTimes));
  ServerCacheHelper.set('openingTimes', openingTimes, A_DAY);
  ServerCacheHelper.set(`ogData-${branchId}`, ogBranchData, A_DAY);
  return Promise.resolve({...defaultOGData, ogBranchData: newOgBranchData});
};

export const loadPackagingPageData = () => {
  return {
    og: true,
    title:
      'Buy Wholesale Takeaway Packaging Wholesales From JJ Foodservice For Delivery or Collection',
    description:
      'View and order online from our range of wholesale Takeaway Packaging at JJ Foodservice. Choose from a wide range of wholesale food packaging supplies & catering containers: takeaway boxes, bags, cups and more',
  };
};

export const loadNotFoundData = () => {
  return Promise.resolve({
    httpCode: 404,
  });
};

export const setAppCleanMode = (match, store) => store.dispatch(setCleanMode());

export const loadBlogCategories = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    // const hostDomain = `${req.protocol}://localhost:8081`;
    const categoriesHomeData = await blogApi.getAllBlogCategories(hostDomain);
    if (categoriesHomeData) {
      store.dispatch({
        type: ActionTypes.GET_BLOG_CATEGORIES_HOME_INFO_SUCCESS,
        categoriesHomeData,
      });
      return Promise.resolve({categories: true});
    }
    return Promise.resolve({categories: false});
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiBlogPostsBySlug = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    // const hostDomain = `${req.protocol}://localhost:8081`;
    const posts = await blogApi.getAllBlogCategoryBySlug(
      match.params.slugName,
      hostDomain
    );
    if (posts) {
      store.dispatch(setBlogCategoryBySlug(posts));
      return Promise.resolve({});
    }
    return Promise.resolve({});
  } catch (e) {
    captureException(e);
  }
};

export const loadWixPost = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    //const hostDomain = `${req.protocol}://${req.hostname}`;
    const blogPost = await blogApi.getBlogPostBySlug(
      match.params.postSlugName,
      hostDomain
    );
    if (blogPost) {
      store.dispatch(setBlogPost(blogPost));
      return Promise.resolve({
        og: true,
        title: blogPost && blogPost.title,
        description: blogPost && blogPost.excerpt,
        image: strapiImage(blogPost).full,
      });
    }
    return Promise.resolve({
      og: true,
      title: 'No blog post',
    });
  } catch (e) {
    captureException(e);
  }
};

export const loadStrapiContentPost = async (match, store, req) => {
  try {
    const hostDomain = 'http://olympus-app-internal:81';
    //const hostDomain = `${req.protocol}://${req.hostname}`;
    const contentPost = await blogApi.getContentPostBySlug(
      match.params.contentSlugName,
      hostDomain
    );
    if (contentPost) {
      store.dispatch(setJJContentBySlug(contentPost));
      return Promise.resolve({
        og: true,
        title: contentPost && contentPost.title,
        description: contentPost && contentPost.excerpt,
        image: strapiImage(contentPost).full,
      });
    }
    return Promise.resolve({
      og: true,
      title: 'No post found',
    });
  } catch (e) {
    captureException(e);
  }
};
